import React, { useEffect, useState } from 'react';
import './tooltip.css';
import { store } from '../../../app/store';
import { postAPI } from '../../../app/api';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { hasPermission, isAdmin } from '../../../store/hooks/Permission';

export default function List({ PopupID = '', setIsEdit, setEditId }) {
  const store_data = JSON.parse(store());
  const [referrals, setReferrals] = useState([]);
  const [selectedReferral, setSelectedReferral] = useState(null);
  const [sendMessage, setSendMessage] = useState('');
  const [isSending, setIsSending] = useState(false);

  const [canView, setCanView] = useState(false);
  const [canEdit, setCanEdit] = useState(false);
  const [canDelete, setCanDelete] = useState(false);

  const fetchReferrals = async () => {
    const response = await postAPI('/referral/list', { org_id: store_data?.org_id, user: store_data });

    if (response?.status === 1) {
      setReferrals(response?.data || []);
    } else {
      setReferrals([]);
    }
  };

  useEffect(() => {
    const checkPermissions = async () => {
      if (isAdmin()) {
        setCanView(true);
        setCanEdit(true);
        setCanDelete(true);
        fetchReferrals();
      } else {
        const viewAllowed = await hasPermission('REFERRALS', 'Referrals', 'VIEW');
        const editAllowed = await hasPermission('REFERRALS', 'Referrals', 'EDIT');
        const deleteAllowed = await hasPermission('REFERRALS', 'Referrals', 'DELETE');

        setCanView(viewAllowed);
        setCanEdit(editAllowed);
        setCanDelete(deleteAllowed);

        if (viewAllowed) {
          fetchReferrals();
        }
      }
    };

    checkPermissions();
  }, []);

  const handleRemove = async (id) => {
    if (!canDelete) {
      toast.error('You do not have permission to perform this action');
      return;
    }

    const response = await postAPI('/referral/remove', { org_id: store_data?.org_id, id, user: store_data });

    if (response?.status === 1) {
      toast.success('Referral removed successfully.');
      fetchReferrals();
    } else {
      toast.error(response?.error || 'Sorry! Something went wrong.');
    }
  };

  const openSendModal = (referral) => {
    setSelectedReferral(referral);
    setSendMessage(referral?.send_message || `Hello ${referral?.name || ''}, you have been referred by Stremax Scholar. Please contact us for more details.`.trim());
  };

  const closeSendModal = () => {
    setSelectedReferral(null);
    setSendMessage('');
    setIsSending(false);
  };

  const handleSend = async () => {
    if (!selectedReferral?.id || isSending) return;

    if (!sendMessage?.trim()) {
      toast.error('Message is required.');
      return;
    }

    setIsSending(true);

    const response = await postAPI('/referral/send', {
      id: selectedReferral.id,
      org_id: store_data?.org_id,
      message: sendMessage,
      user: store_data,
    });

    if (response?.status === 1) {
      toast.success('Referral SMS sent successfully.');
      closeSendModal();
      fetchReferrals();
    } else {
      toast.error(response?.error || 'Sorry! Something went wrong.');
      setIsSending(false);
    }
  };

  const handleToggleStatus = async (referral) => {
    const nextStatus = Number(referral?.status) === 1 ? 0 : 1;
    const response = await postAPI('/referral/toggle-status', {
      id: referral?.id,
      status: nextStatus,
      user: store_data,
    });

    if (response?.status === 1) {
      toast.success('Referral status updated successfully.');
      fetchReferrals();
    } else {
      toast.error(response?.error || 'Sorry! Something went wrong.');
    }
  };

  return (
    <>
      <ToastContainer />
      <table className="table card-table">
        <thead>
          <tr>
            <th>Name</th>
            <th>Phone Number</th>
            <th>User Unique ID</th>
            <th>Status</th>
            <th>Send Status</th>
            <th>Sent Time</th>
            <th style={{ width: '260px' }}>Actions</th>
          </tr>
        </thead>
        <tbody className="table-border-bottom-0">
          {!canView ? (
            <tr>
              <td colSpan="7" className="text-center text-danger py-4">
                You do not have permission to view this page.
              </td>
            </tr>
          ) : referrals.length > 0 ? (
            referrals.map((val, index) => (
              <tr key={val?.id || index}>
                <td>{val?.name || '-'}</td>
                <td>{val?.phone_no || '-'}</td>
                <td>{val?.user_unique_id || '-'}</td>
                <td>
                  {Number(val?.status) === 1 ? (
                    <span className="badge bg-label-success">Active</span>
                  ) : (
                    <span className="badge bg-label-secondary">Inactive</span>
                  )}
                </td>
                <td>
                  {val?.send_status === 'SENT' ? (
                    <span className="badge bg-label-success">Sent</span>
                  ) : val?.send_status === 'FAILED' ? (
                    <span className="badge bg-label-danger">Failed</span>
                  ) : (
                    <span className="badge bg-label-warning">Pending</span>
                  )}
                </td>
                <td>{val?.send_time ? new Date(val.send_time).toLocaleString() : '-'}</td>
                <td>
                  {canEdit && val?.phone_no && (
                    <span
                      className="badge bg-label-success me-1 custom-tooltip"
                      data-bs-toggle="modal"
                      data-bs-target="#ReferralSendModal"
                      onClick={() => openSendModal(val)}
                    >
                      <i className="fa fa-paper-plane"></i>
                      <span className="tooltip-text">Send Referral SMS</span>
                    </span>
                  )}

                  {canEdit && (
                    <span
                      className="badge bg-label-primary me-1 custom-tooltip"
                      data-bs-toggle="modal"
                      data-bs-target={`#${PopupID}`}
                      onClick={() => {
                        setIsEdit(true);
                        setEditId(val?.id);
                      }}
                    >
                      <i className="fa fa-pen"></i>
                      <span className="tooltip-text">Edit Referral</span>
                    </span>
                  )}

                  {canEdit && (
                    <span
                      className={`badge ${Number(val?.status) === 1 ? 'bg-label-warning' : 'bg-label-info'} me-1 custom-tooltip cursor-pointer`}
                      onClick={() => handleToggleStatus(val)}
                    >
                      <i className={`fa ${Number(val?.status) === 1 ? 'fa-ban' : 'fa-check'}`}></i>
                      <span className="tooltip-text">{Number(val?.status) === 1 ? 'Deactivate Referral' : 'Activate Referral'}</span>
                    </span>
                  )}

                  {canDelete && (
                    <span
                      className="badge bg-label-danger me-1 cursor-pointer custom-tooltip"
                      onClick={() => {
                        if (window.confirm('Are you sure? This action will permanently remove this data!')) {
                          handleRemove(val?.id);
                        }
                      }}
                    >
                      <i className="fa fa-trash"></i>
                      <span className="tooltip-text">Delete Referral</span>
                    </span>
                  )}
                </td>
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan="7" className="text-center py-4">No referrals found.</td>
            </tr>
          )}
        </tbody>
      </table>

      <div className="modal fade" id="ReferralSendModal" tabIndex="-1" aria-hidden="true">
        <div className="modal-dialog modal-lg modal-simple modal-edit-user">
          <div className="modal-content p-3 p-md-5">
            <div className="modal-body">
              <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close" onClick={closeSendModal}></button>
              <div className="text-center mb-4">
                <h3 className="mb-2">Send Referral SMS</h3>
                <p className="text-muted mb-0">
                  {selectedReferral?.phone_no
                    ? `This message will be sent to ${selectedReferral?.phone_no}.`
                    : 'This referral does not have a phone number.'}
                </p>
              </div>

              <div className="row g-3">
                <div className="col-12 col-md-6">
                  <label className="form-label">Name</label>
                  <input className="form-control" value={selectedReferral?.name || ''} disabled />
                </div>
                <div className="col-12 col-md-6">
                  <label className="form-label">Phone Number</label>
                  <input className="form-control" value={selectedReferral?.phone_no || ''} disabled />
                </div>

                <div className="col-12">
                  <label className="form-label">Message</label>
                  <textarea
                    className="form-control"
                    rows="6"
                    value={sendMessage}
                    onChange={(e) => setSendMessage(e.target.value)}
                    placeholder="Enter SMS message"
                  ></textarea>
                </div>

                {selectedReferral?.send_response && (
                  <div className="col-12">
                    <label className="form-label">Last Send Response</label>
                    <div className="border rounded p-3 bg-light" style={{ maxHeight: '220px', overflowY: 'auto', whiteSpace: 'pre-wrap' }}>
                      {(() => {
                        try {
                          const response = JSON.parse(selectedReferral.send_response);
                          return JSON.stringify(response, null, 2);
                        } catch (error) {
                          return selectedReferral.send_response;
                        }
                      })()}
                    </div>
                  </div>
                )}

                <div className="col-12 text-center mt-3">
                  <button type="button" className="btn btn-primary me-2" onClick={handleSend} disabled={isSending || !selectedReferral?.phone_no}>
                    {isSending ? 'Sending...' : 'Send SMS'}
                  </button>
                  <button type="button" className="btn btn-label-secondary" data-bs-dismiss="modal" onClick={closeSendModal}>Cancel</button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
